import { useEffect, useMemo, useState } from "react";
import { Button, Popconfirm, Table, Tag, message } from "antd";
import moment from "moment";
import { useSelector } from "react-redux";
import usersService, { SystemUser } from "../../services/users.service";
import { UserRole } from "../../services/roles";
import useAuthenthication from "../../hooks/useAuthentication";
import { IUser } from "../../models/user";
import { useI18n } from "../../i18n/I18nContext";

const roleColor: Record<UserRole, string> = {
  admin: "purple",
  operator: "green",
  manager: "blue",
};

function UserDeleteComponent({
  userId,
  username,
}: {
  userId: string;
  username: string;
}) {
  const { t } = useI18n();
  const { logoutCall } = useAuthenthication();
  const user = useSelector(({ UserSlice }: any) => UserSlice.user as IUser);
  const [open, setOpen] = useState(false);
  const [confirmLoading, setConfirmLoading] = useState(false);

  const handleOk = async () => {
    setConfirmLoading(true);
    try {
      await usersService.deleteUser(userId);
      if (user?.email === username || user?.username === username) {
        logoutCall();
        return;
      }
      message.success("OK", 4);
      setTimeout(() => window.location.reload(), 1500);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        t("asi.saveError");
      message.error(
        Array.isArray(msg) ? msg.join(", ") : String(msg),
        8
      );
    } finally {
      setOpen(false);
      setConfirmLoading(false);
    }
  };

  return (
    <Popconfirm
      title={t("users.deleteTitle")}
      description={t("users.deleteConfirm")}
      open={open}
      onConfirm={handleOk}
      okButtonProps={{ loading: confirmLoading, danger: true }}
      onCancel={() => {
        setOpen(false);
        setConfirmLoading(false);
      }}
      cancelText={t("users.cancel")}
      okText={t("users.delete")}
    >
      <Button danger onClick={() => setOpen(true)}>
        {t("users.delete")}
      </Button>
    </Popconfirm>
  );
}

const OperatorList = () => {
  const { t } = useI18n();
  const [operators, setOperators] = useState<SystemUser[]>([]);
  const [loading, setLoading] = useState(false);

  const roleLabel = useMemo(
    () =>
      ({
        admin: t("users.roleLabel.admin"),
        operator: t("users.roleLabel.operator"),
        manager: t("users.roleLabel.manager"),
      }) as Record<UserRole, string>,
    [t]
  );

  const columns = useMemo(
    () => [
      {
        title: t("users.col.createdAt"),
        key: "createdAt",
        render: (_: any, u: SystemUser) => (
          <div>
            {u.createdAt ? moment(u.createdAt).format("DD-MM-YYYY") : "—"}
          </div>
        ),
      },
      {
        title: t("users.col.login"),
        dataIndex: "username",
        key: "username",
      },
      {
        title: t("users.col.role"),
        dataIndex: "role",
        key: "role",
        render: (role: UserRole) => (
          <Tag color={roleColor[role] || "default"}>
            {roleLabel[role] || role}
          </Tag>
        ),
      },
      {
        title: t("users.col.actions"),
        key: "actions",
        render: (_: any, u: SystemUser) => (
          <UserDeleteComponent userId={u._id} username={u.username} />
        ),
      },
    ],
    [t, roleLabel]
  );

  const fetchOperators = async () => {
    setLoading(true);
    try {
      const users = await usersService.getUsersList();
      setOperators(users || []);
    } catch (error: any) {
      const msg =
        error?.response?.data?.message ||
        error?.message ||
        t("asi.saveError");
      message.error(
        Array.isArray(msg) ? msg.join(", ") : String(msg),
        8
      );
      setOperators([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOperators();
  }, []);

  return (
    <div>
      <Table
        size="small"
        dataSource={operators}
        columns={columns}
        loading={loading}
        rowKey={(i) => i._id}
        locale={{ emptyText: t("users.empty") }}
      />
    </div>
  );
};

export default OperatorList;
